/**
 * Pending shots — shared by the Course Flyover rangefinder and the Score Entry
 * round flow.
 *
 * The Flyover page lets a Pro user drop a "Track shot from here" pin while
 * the round is being played. Because rounds don't exist server-side until
 * the player submits the full 18-hole card, we stash the shot lat/lng + club
 * in localStorage keyed by `bula.pendingShots`. Score Entry reads + clears
 * them on mount so they get appended to the round POST without the player
 * having to log each one twice.
 */
const KEY = "bula.pendingShots";

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}

function writeAll(obj) {
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch { /* ignore quota */ }
}

/** Append a single shot to the pending bucket for {courseId, hole}. */
export function pushPendingShot({ courseId, hole, lat, lng, club }) {
  if (!courseId || !hole || lat == null || lng == null) return;
  const all = readAll();
  const courseBucket = all[courseId] || {};
  const holeBucket = courseBucket[String(hole)] || [];
  holeBucket.push({ lat, lng, club: club || "Driver", ts: Date.now() });
  courseBucket[String(hole)] = holeBucket;
  all[courseId] = courseBucket;
  writeAll(all);
}

/** Return all pending shots for a course as a flat [{hole, lat, lng, club}] list. */
export function getPendingShots(courseId) {
  const all = readAll();
  const courseBucket = all[courseId] || {};
  const out = [];
  Object.entries(courseBucket).forEach(([hole, list]) => {
    list.forEach((s) => out.push({ hole: Number(hole), lat: s.lat, lng: s.lng, club: s.club }));
  });
  return out;
}

/** Clear pending shots for a course (called after round submit). */
export function clearPendingShots(courseId) {
  const all = readAll();
  if (!all[courseId]) return;
  delete all[courseId];
  writeAll(all);
}

/** Count of pending shots across all courses — used by header badge / debug. */
export function pendingShotCount(courseId) {
  return getPendingShots(courseId).length;
}
